// frontend/src/components/dashboard/ChecklistManager.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, ClipboardDocumentCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';

const ChecklistManager = ({ groupId, membership, user }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [newChecklist, setNewChecklist] = useState({
    title: '',
    category: 'packing',
    items: [
      { text: 'Passport/ID', completed: false },
      { text: 'Tickets/Bookings', completed: false },
      { text: 'Medications', completed: false }
    ]
  });
  
  useEffect(() => {
    loadChecklists();
  }, [groupId]);
  
  const loadChecklists = async () => {
    try {
      setLoading(true);
      const checklistsData = await groupDashboardService.getChecklists(groupId);
      setChecklists(checklistsData);
    } catch (error) {
      console.error('Failed to load checklists:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddChecklist = async (e) => {
    e.preventDefault();
    try {
      await groupDashboardService.addChecklist(groupId, newChecklist);
      
      setNewChecklist({
        title: '',
        category: 'packing',
        items: [
          { text: 'Passport/ID', completed: false },
          { text: 'Tickets/Bookings', completed: false },
          { text: 'Medications', completed: false }
        ]
      });
      setShowAddForm(false);
      loadChecklists();
    } catch (error) {
      console.error('Failed to add checklist:', error);
    }
  };
  
  const handleUpdateItem = async (checklistId, itemIndex, completed) => {
    try {
      await groupDashboardService.updateChecklistItem(groupId, checklistId, {
        itemIndex,
        completed
      });
      loadChecklists();
    } catch (error) {
      console.error('Failed to update checklist item:', error);
    }
  };
  
  const addItem = () => {
    setNewChecklist({
      ...newChecklist,
      items: [...newChecklist.items, { text: '', completed: false }]
    });
  };
  
  const updateItemText = (index, text) => {
    const newItems = [...newChecklist.items];
    newItems[index].text = text;
    setNewChecklist({ ...newChecklist, items: newItems });
  };
  
  const removeItem = (index) => {
    const newItems = newChecklist.items.filter((_, i) => i !== index);
    setNewChecklist({ ...newChecklist, items: newItems });
  };
  
  const categories = [
    { id: 'packing', name: 'Packing', color: 'bg-blue-100 text-blue-800' },
    { id: 'documents', name: 'Documents', color: 'bg-green-100 text-green-800' },
    { id: 'preparations', name: 'Preparations', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'purchases', name: 'Purchases', color: 'bg-purple-100 text-purple-800' },
    { id: 'tasks', name: 'Tasks', color: 'bg-pink-100 text-pink-800' },
    { id: 'other', name: 'Other', color: 'bg-gray-100 text-gray-800' }
  ];
  
  const calculateCompletion = (checklist) => {
    if (!checklist.items || checklist.items.length === 0) return 0;
    const completed = checklist.items.filter(item => item.completed).length;
    return Math.round((completed / checklist.items.length) * 100);
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading checklists...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Trip Checklists</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-2 rounded-lg"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Checklist
        </Button>
      </div>
      
      {/* Create Checklist Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
          <h3 className="font-bold text-gray-800 mb-4 text-lg">Create New Checklist</h3>
          
          <form onSubmit={handleAddChecklist} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Checklist Title
                </label>
                <input
                  type="text"
                  value={newChecklist.title}
                  onChange={(e) => setNewChecklist({ ...newChecklist, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Essential Packing List"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={newChecklist.category}
                  onChange={(e) => setNewChecklist({ ...newChecklist, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Checklist Items
                </label>
                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm text-purple-600 hover:text-purple-800"
                >
                  + Add Item
                </button>
              </div>
              
              {newChecklist.items.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={item.text}
                    onChange={(e) => updateItemText(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={`Item ${index + 1}`}
                    required
                  />
                  {newChecklist.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-2 rounded-lg"
              >
                Create Checklist
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Checklists List */}
      <div className="space-y-6">
        {checklists.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardDocumentCheckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Checklists Yet</h3>
            <p className="text-gray-600 mb-6">Create checklists to stay organized!</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-lg"
            >
              Create First Checklist
            </Button>
          </div>
        ) : (
          checklists.map((checklist) => {
            const completion = calculateCompletion(checklist);
            const category = categories.find(c => c.id === checklist.category);
            
            return (
              <div key={checklist._id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${category?.color}`}>
                        {category?.name}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{checklist.title}</h3>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Completion</div>
                    <div className="text-2xl font-bold text-purple-600">{completion}%</div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-500"
                      style={{ width: `${completion}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {checklist.items.filter(item => item.completed).length} of {checklist.items.length} items completed
                  </div>
                </div>
                
                {/* Checklist Items */}
                <div className="space-y-2">
                  {checklist.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <button
                        onClick={() => handleUpdateItem(checklist._id, index, !item.completed)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          item.completed
                            ? 'border-green-500 bg-green-100'
                            : 'border-gray-300 hover:border-purple-500'
                        }`}
                      >
                        {item.completed && (
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        )}
                      </button>
                      <span className={`flex-1 ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-2">
                      {checklist.createdBy?.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">{checklist.createdBy?.name}</p>
                      <p className="text-xs text-gray-500">
                        Created {new Date(checklist.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    {checklist.isShared ? 'Shared with group' : 'Personal checklist'}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChecklistManager;